<template>
  <b-card class="mb-2">
    <template #header>
      <div class="d-flex align-items-center justify-content-between">
        <h4 class="mb-0">{{ $t('Add document') }}</h4>
        <feather-icon icon="ChevronRightIcon" size="18" />
      </div>
    </template>

    <div class="mb-2">
      <!-- Proforma Invoice Section -->
      <div class="d-flex align-items-center mb-1">
        <div class="d-flex align-items-center flex-grow-1">
          <div
            class="d-flex align-items-center justify-content-center bg-light-primary rounded mr-1"
            style="width: 40px; height: 40px"
          >
            <feather-icon icon="FileTextIcon" size="20" class="text-primary" />
          </div>
          <h6 class="mb-0">
            {{ $t('You can add documents to your shipment or request them to be created.') }}
          </h6>
        </div>
      </div>
      <div class="mb-1 border rounded p-1">
        <div>
          <!-- Option 1: Generate Document Checkbox -->
          <b-form-checkbox
            v-model="generateDocument"
            class="mb-1"
            :disabled="!!uploadedDocumentId"
            @change="handleGenerateChange"
          >
            {{ $t('I want the Document to be created automatically') }}
          </b-form-checkbox>

          <!-- Option 2: Upload Document -->
          <div v-if="!generateDocument">
            <div class="d-flex align-items-center mb-1">
              <span class="text-muted mr-1">- {{ $t('Or') }} -</span>
            </div>

            <!-- Document Reference Input -->
            <b-form-group label-for="document-reference">
              <template #label>
                {{ $t('Reference No') }}
                <small class="text-muted font-weight-normal"> ({{ $t('Optional') }}) </small>
              </template>
              <b-form-input
                id="reference-no"
                v-model="documentReference"
                style="max-width: 50%"
                :placeholder="$t('Reference No')"
                :disabled="isUploading || !!uploadedDocumentId"
              />
            </b-form-group>

            <b-form-group label-for="document-upload">
              <template #label>
                {{ $t('Upload Document') }}
                <small class="text-muted d-block font-weight-normal">
                  (PDF, JPG, PNG - Max 5MB)
                </small>
              </template>
              <div class="d-flex">
                <b-form-file
                  id="document-upload"
                  v-model="file"
                  style="max-width: 50%"
                  :placeholder="$t('Choose a file or drop it here')"
                  :drop-placeholder="$t('Drop file here')"
                  accept=".pdf, .jpg, .jpeg, .png"
                  :disabled="isUploading || !!uploadedDocumentId"
                  @input="handleFileUpload"
                />
                <b-button
                  v-if="uploadedDocumentId"
                  variant="danger"
                  class="ml-1"
                  size="sm"
                  @click="removeUploadedDocument"
                >
                  <feather-icon icon="Trash2Icon" size="16" />
                </b-button>
              </div>
              <small v-if="uploadError" class="text-danger">{{ uploadError }}</small>
              <small v-if="uploadedDocumentId" class="text-success">{{
                $t('Document uploaded successfully')
              }}</small>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BCard, BFormCheckbox, BFormGroup, BFormFile, BButton, BFormInput } from 'bootstrap-vue';
import useService from '@/auth/service/useService';

export default {
  name: 'ConsignmentDocumentServices',
  components: {
    BCard,
    BFormCheckbox,
    BFormGroup,
    BFormFile,
    BButton,
    BFormInput
  },
  props: {
    referenceNo: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      generateDocument: false,
      file: null,
      isUploading: false,
      uploadedDocumentId: null,
      uploadError: null,
      documentReference: ''
    };
  },
  methods: {
    handleGenerateChange(checked) {
      if (checked) {
        this.$emit('update:generateDocuments', [{ type: 'PROFORMA_INVOICE' }]);
        this.$emit('update:documents', []);
        this.resetUpload();
      } else {
        this.$emit('update:generateDocuments', []);
      }
    },
    async handleFileUpload(file) {
      if (!file) return;

      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        'application/pdf',
        'application/x-pdf',
        'image/jpeg',
        'image/png',
        'application/octet-stream'
      ];

      if (file.size > maxSize || !allowedTypes.includes(file.type)) {
        this.uploadError = this.$t(
          file.size > maxSize ? 'uploadedFileTooLarge' : 'uploadedFileNotValidType',
          {
            fileName: file.name,
            fileSize: '5MB',
            fileType: 'PDF, JPEG, PNG'
          }
        );
        this.file = null;
        return;
      }

      this.isUploading = true;
      this.uploadError = null;

      try {
        const formData = new FormData();
        formData.append('document_file', file);
        formData.append('document_type', '13'); // 13 for Proforma Invoice
        formData.append('document_issued_at', new Date().toISOString().split('T')[0]);

        const docRef = this.documentReference || this.referenceNo;
        if (docRef) formData.append('document_reference', docRef);

        const response = await useService.uploadConsignmentDocument(formData);
        this.uploadedDocumentId = response?.data?.id || response?.id;

        if (!this.uploadedDocumentId) throw new Error('ID not returned');

        this.$emit('update:documents', [{ id: this.uploadedDocumentId }]);
        this.$emit('update:generateDocuments', []);
      } catch (error) {
        this.uploadError = error.message || this.$t('Upload failed');
        this.file = null;
      } finally {
        this.isUploading = false;
      }
    },
    removeUploadedDocument() {
      this.uploadedDocumentId = null;
      this.file = null;
      this.$emit('update:documents', []);
    },
    resetUpload() {
      this.file = null;
      this.uploadedDocumentId = null;
      this.uploadError = null;
      this.documentReference = '';
    }
  }
};
</script>
